// eslint-disable-next-line no-restricted-imports
import { FormApi } from 'final-form'
import { FieldMetaState } from 'react-final-form'
import { MetaPath } from 'util/metaPath'
import { ErrorObject } from 'util/validation'

import { PecFormApi } from './model'

/**
 * Converte o FormApi da lib no PecFormApi, cujas operações por nome de campo aceitam string.
 *
 * Só é preciso no retorno de `useForm<Model>()`: o generic faz o final-form exigir `keyof Model`, que
 * recusa os caminhos aninhados do metaPath. Sem generic, o useForm já devolve api que aceita string.
 */
export function toPecFormApi<FormValues>(form: FormApi<FormValues>): PecFormApi<FormValues> {
  return (form as unknown) as PecFormApi<FormValues>
}

/**
 * Retorna o erro ativo do campo.
 *
 * @param meta O meta state do campo, do react-final-form.
 */
export const getFieldError = (meta: FieldMetaState<any>): any => {
  return (meta.touched && meta.error) || (!meta.dirtySinceLastSubmit && meta.submitError)
}

/**
 * Retorna o erro ativo do campo, sem verificar se ele está touched ou dirty.
 *
 * @param meta O meta state do campo, do react-final-form.
 */
export const getSimpleFieldError = (meta: FieldMetaState<any>): any => meta.error || meta.submitError

/**
 * Reseta o estado (touched, visited, ...) de todos os campos registrados.
 */
export const resetAllFieldStates = (form: PecFormApi) => {
  form.getRegisteredFields().forEach((field) => form.resetFieldState(field))
}

/**
 * Retorna true se algum campo do formulário está marcado como touched.
 *
 * @param formApi O PecFormApi do formulário.
 */
export const anyFieldIsTouched = (formApi: PecFormApi): boolean => {
  return (
    formApi
      .getRegisteredFields()
      .map((field) => formApi.getFieldState(field)?.touched)
      .filter((value) => value).length > 0
  )
}

/**
 * Retorna o ErrorObject dos campos do grupo, pelo nome.
 *
 * @param name O nome, em MetaPath, dos campos do grupo.
 * @param formApi O PecFormApi do formulário.
 * @param ignoreTouchedAndDirty Se true, retorna os erros mesmo dos campos que não estão touched nem dirty.
 */
export const getFormGroupErrors = (
  name: MetaPath<any>,
  formApi: PecFormApi,
  ignoreTouchedAndDirty?: boolean
): ErrorObject<any> => {
  const absolutePath = name.absolutePath()

  if (!absolutePath) return null

  const extractFieldError = ignoreTouchedAndDirty ? getSimpleFieldError : getFieldError

  return Object.fromEntries(
    formApi
      .getRegisteredFields()
      .filter((fieldName) => fieldName.includes(absolutePath))
      .map((fieldName) => {
        const fieldState = formApi.getFieldState(fieldName)
        return [fieldName.replace(`${absolutePath}.`, ''), fieldState && extractFieldError(fieldState)]
      })
  )
}
